// Wires a capture client's lifecycle for a capture screen: registers it with
// the logout flush registry on mount and tears it down (destroy) on unmount.
//
// #726: the registry is the global handle the logout flow uses to drain every
// mounted client's in-memory 500 ms coalesce to durable storage BEFORE the
// held-count guard reads the device — so a +/- tap taken just before sign-out
// is persisted (and surfaces in the loud warning) instead of being silently
// destroyed by the sign-out clear. Centralised here so all four capture mount
// sites get identical, StrictMode-correct behaviour from one call rather than
// duplicating two effects each.

import { useEffect, useMemo, useRef } from 'react'

import type { CaptureClient } from '@citycare/shared'

import type { CaptureClientWithInternals } from './capture-client'
import { createSelectedCaptureClient } from './capture-client-factory'
import {
  registerCaptureClient,
  retainPendingFlush,
} from './capture-client-registry'

/**
 * The widest client shape this hook can wire. `flush` / `destroy` are optional
 * so a test-injected factory (RoomCaptureScreen's `createClient` prop, a
 * `CaptureClient & { destroy?: () => void }`) satisfies the constraint without
 * a cast — the hook guards each capability at call time. The real factory
 * (`createSelectedCaptureClient`) returns a full `CaptureClientWithInternals`,
 * so the default instantiation keeps the strict return type for the three
 * screens that take no factory.
 */
type RegisterableCaptureClient = CaptureClient & {
  flush?: () => Promise<void>
  destroy?: () => void | Promise<void>
}

/**
 * Create one capture client for this screen's lifetime, register it with the
 * logout flush registry, destroy it on unmount, and return it. Replaces the
 * `useMemo(() => createSelectedCaptureClient(), [])` each capture screen had,
 * folding in the #726 registration so every mount site behaves identically.
 * All four capture screens (room, site, building, building-content) share this
 * one implementation — RoomCaptureScreen passes its test-injectable factory
 * through `createClient`; the other three take the default.
 *
 * - Register: `registerCaptureClient` returns its own unregister fn, used as
 *   the effect cleanup. Correct under React StrictMode's mount→cleanup→mount
 *   double-invoke — register → unregister → register leaves exactly one
 *   registration (the registry Set also dedupes the same instance). Only a
 *   client that actually exposes `flush()` is registered (the loud-warning
 *   flush contract needs it); a factory without it — only possible via the
 *   test seam — is wired for destroy-on-unmount but never registered.
 * - Destroy: tears down the client's pending retry / coalesce timers on
 *   unmount — the pre-existing per-screen cleanup. Guarded because the test
 *   seam's type leaves `destroy` optional.
 *
 * Two separate effects (not one) so the register cleanup runs on every
 * StrictMode re-invoke while destroy is delayed long enough for the same
 * mounted client to cancel the development replay cleanup.
 *
 * @param createClient factory for this screen's client; defaults to the
 *   env-gated REST/PowerSync factory used by every non-test mount.
 */
export function useRegisteredCaptureClient<
  TClient extends RegisterableCaptureClient = CaptureClientWithInternals,
>(
  // The default value is asserted to `() => TClient` because a concrete
  // `() => CaptureClientWithInternals` is not statically provable as
  // `() => TClient` for a caller-chosen `TClient` (a generic-default
  // limitation, not the optional-flush gap). It is sound: the default is only
  // ever used when no argument is passed, which pins `TClient` to its
  // `CaptureClientWithInternals` default — exactly the factory's return type.
  createClient: () => TClient = createSelectedCaptureClient as () => TClient,
): TClient {
  const client = useMemo(() => createClient(), [createClient])
  const pendingDestroy = useRef<{
    client: TClient
    token: symbol
  } | null>(null)

  useEffect(() => {
    if (typeof client.flush !== 'function') return undefined
    return registerCaptureClient({ flush: client.flush.bind(client) })
  }, [client])
  useEffect(() => {
    if (pendingDestroy.current?.client === client) pendingDestroy.current = null
    return () => {
      const destroy = client.destroy
      if (typeof destroy !== 'function') return
      const token = Symbol('capture-client-destroy')
      pendingDestroy.current = { client, token }
      queueMicrotask(() => {
        if (
          pendingDestroy.current?.client !== client ||
          pendingDestroy.current.token !== token
        ) {
          return
        }
        pendingDestroy.current = null
        // #810 P1: destroy() best-effort-flushes the open coalesce window (#817)
        // and RETURNS that flush promise. Retain it with the registry so the
        // capture error-boundary Reload — which runs after this screen has
        // unmounted and unregistered — still awaits the in-flight drain instead
        // of reloading over a not-yet-persisted tap. The REST delegate's destroy
        // returns void, so there is nothing to retain on that path.
        const flushed: void | Promise<void> = destroy()
        if (
          flushed != null &&
          typeof (flushed as Promise<void>).then === 'function'
        ) {
          retainPendingFlush(flushed as Promise<void>)
        }
      })
    }
  }, [client])
  return client
}
